import { isEqual, uniq } from "lodash";
import moment from "moment";
import { Component, Fragment, JSX } from "react";
import { RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import sortBy from "lodash/sortBy";

import { ExportType } from "@app/exports/enumerations";
import { ProjectPerspective, ProjectRunConfigurationResult, ProjectService } from "@app/projects";
import {
  FilterType,
  ImpactIndicator,
  NetworkAssessmentAnalysisType,
  ProjectRunConfigurationStatus,
  ProjectRunConfigurationType,
  ProviderNetworkType
} from "@app/projects/enumerators";
import ProjectRunConfiguration from "@app/projects/projectRunConfiguration";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import { ResultRegion, SpecialtyGroupException, SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { ImpactProvidersPanel, NetworkProvidersPanel } from "@app/projects/servicing-provider";
import { NetworkProvidersComparisonPanel } from "@app/projects/servicing-provider/NetworkProvidersComparisonPanel";
import { RegionGranularity } from "@app/region-sets";
import SpecialtyGroupExceptionEditor from "@app/projects/network-assessment/SpecialtyGroupExceptionEditor";

import { AdequacyDetail, getTimeString } from "./";

import InformationalIndicator from "@app/projects/network-assessment/InformationalSpecialtyGroupIndicator";
import SpecialtyGroupExceptionTimelineLink from "@app/projects/network-assessment/SpecialtyGroupExceptionTimelineLink";

import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { ZipSummariesPanel } from "@app/projects/zip-summaries";
import { formatDateCustom, sortByDate, TooltipWrapper } from "@app/shared";
import { Panel as SkeletonPanel, PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import {
  ColumnConfig,
  ColumnFilter,
  ColumnGroup,
  genericFilter,
  numberSort,
  printNumberAsString,
  SortDirections,
  TextAlignment,
  WafflesFilterType
} from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { TemplateCategory, TemplateService } from "@app/templates";
import { ViewableFieldsContextProps, withViewableFields } from "@app/viewable-fields";
import { TableType } from "@app/viewable-fields/viewableFields";
import { NextClosestImpactProvidersPanel } from "@app/projects/servicing-provider/NextClosestImpactProvidersPanel";
import { isNullOrUndefined } from "@app/shared/utility";
import {
  cmsCreditDataAlert,
  cmsCreditIconToggleApply,
  cmsCreditIconToggleChanges,
  cmsCreditProvidersAlert
} from "./CMSCreditComponents";
import { CalculationMethodEnum } from "@app/templates/templateModels";
import { getFillableGapsIndicatorTooltip, getMarketAnalysisTooltip, getServicingGranularityHeader, showPatternOfCare } from "./utility";
import { FeatureFlagContextProps, Features, withFeatureFlagContext } from "@app/feature-flags";
import { NetNewServicingProvidersPanel } from "@app/projects/servicing-provider/NetNewServicingProvidersPanel";
import GapFillStatusEditor from "../GapFillStatusEditor";
import AdequacyDetailInfoTemplate from "./AdequacyDetailInfoTemplate";
import NetNewServicingProviderService from "@app/projects/servicing-provider/NetNewServicingProviderService";
import { Roles, UserContextProps, withUserContext } from "@app/user";
import {
  PatternOfCareProvidersPanel
} from "@app/projects/servicing-provider/pattern-of-care-providers/PatternOfCareProvidersPanel";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { GapFillStatusDescriptionProps } from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import {
  withGapFillStatusDescriptions
} from "@app/projects/network-assessment/adequacy/hooks/useGapFillStatusDescriptions";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { exportTypeToReportTypeMap } from "@app/report-customization/build-reports/helpers";

type AdequacyDetailsProps = RouteComponentProps<unknown, unknown, ProjectRouteParams> &
  CountyProjectContextProps &
  ViewableFieldsContextProps &
  FeatureFlagContextProps &
  UserContextProps &
  GapFillStatusDescriptionProps &
{
  exportComparedRunConfigurationId?: number;
  exportEndpoint: string;
  mappingEndpoint: string;
};

export class AdequacyDetailWaffleToppings {
  public accessAvgDist: string;
  public accessAvgDist2: string;
  public accessAvgDistWith: string;
  public accessAvgDist2With: string;
  public accessAvgDistWithout: string;
  public accessAvgDist2Without: string;
  public accessMaxDist: string;
  public accessMaxDistWith: string;
  public accessMaxDistWithout: string;
  public accessAvgTime: string;
  public accessAvgTime2: string;
  public accessAvgTimeWith: string;
  public accessAvgTime2With: string;
  public accessAvgTimeWithout: string;
  public accessAvgTime2Without: string;
  public accessMaxTime: string;
  public accessMaxTimeWith: string;
  public accessMaxTimeWithout: string;
  public accessMet: string;
  public accessMetDist: string;
  public accessMetTime: string;
  public accessTelehealthCredit: string;
  public accessWith: string;
  public accessWithDist: string;
  public accessWithTime: string;
  public accessWithout: string;
  public accessWithoutDist: string;
  public accessWithoutTime: string;
  public attestationPercent: string;
  public conAccessCreditPercentage: string;
  public countyClass: string;
  public countySSA: string;
  public fillableGapIndicator: string;
  public gapFillStatus: string;
  public gapFillStatusUpdatedByName: string;
  public gapFillStatusUpdatedDate: string;
  public key: string;
  public marketAccess: string;
  public marketDistinct: string;
  public marketPercentage: string;
  public marketProviderCount: string;
  public fullMembershipCount: string;
  public maxReqdDistance: string;
  public maxReqdTime: string;
  public fullMemberCountWithAccess: string;
  public fullMemberCountWithoutAccess: string;
  public membershipName: JSX.Element;
  public overallGapAge: string;
  public overallMet: string;
  public prospectImpact: JSX.Element;
  public resultsFor: string;
  public servicingDistinct: string;
  public servicingMet: string;
  public servicingProviderCount: string;
  public servicingProviderToMemberRatio: string;
  public servicingRequired: string;
  public title: JSX.Element;
  public gapFillStatusComment: string;
  constructor(key: string, resultsFor: string) {
    /* eslint-disable sort-keys */
    // The order of these property assignments must match the order of the columns in
    // AdequacyDetails.getColumnConfig() for compare mode to display properly.
    this.key = key;
    this.title = null;
    this.countyClass = "---";
    this.membershipName = null;
    this.fullMembershipCount = "---";
    this.maxReqdDistance = "---";
    this.maxReqdTime = "---";
    this.resultsFor = resultsFor;
    this.countySSA = "---";
    this.fullMemberCountWithAccess = "---";
    this.accessWith = "---";
    this.fullMemberCountWithoutAccess = "---";
    this.accessWithout = "---";
    this.accessAvgDist = "---";
    this.accessAvgDistWith = "---";
    this.accessAvgDistWithout = "---";
    this.accessAvgDist2 = "---";
    this.accessAvgDist2With = "---";
    this.accessAvgDist2Without = "---";
    this.accessMaxDist = "---";
    this.accessMaxDistWith = "---";
    this.accessMaxDistWithout = "---";
    this.accessWithDist = "---";
    this.accessWithoutDist = "---";
    this.accessAvgTime = "---";
    this.accessAvgTimeWith = "---";
    this.accessAvgTimeWithout = "---";
    this.accessAvgTime2 = "---";
    this.accessAvgTime2With = "---";
    this.accessAvgTime2Without = "---";
    this.accessMaxTime = "---";
    this.accessMaxTimeWith = "---";
    this.accessMaxTimeWithout = "---";
    this.accessWithTime = "---";
    this.accessWithoutTime = "---";
    this.accessMetDist = "---";
    this.accessMetTime = "---";
    this.accessMet = "---";
    this.accessTelehealthCredit = "---";
    this.conAccessCreditPercentage = "---";
    this.servicingRequired = "---";
    this.servicingProviderCount = "---";
    this.servicingProviderToMemberRatio = "---";
    this.servicingDistinct = "---";
    this.servicingMet = "---";
    this.overallMet = "---";
    this.overallGapAge = "---";
    this.marketAccess = "---";
    this.marketProviderCount = "---";
    this.marketDistinct = "---";
    this.marketPercentage = "---";
    this.fillableGapIndicator = "";
    this.prospectImpact = <span key="0" />;
    this.attestationPercent = "---";
    this.gapFillStatus = "---";
    this.gapFillStatusUpdatedDate = "---";
    this.gapFillStatusUpdatedByName = "---";
    this.gapFillStatusComment = "---";
    /* eslint-enable sort-keys */
  }
}

interface ResultsView {
  showMarket: boolean;
  showNetwork: boolean;
  showNetworkComparison: boolean;
  showNextClosestNetworkProviders: boolean;
  showNextClosestProspectProviders: boolean;
  showNextClosestMarketProviders: boolean;
  showPatternOfCareNetworkProviders: boolean;
  showProspect: boolean;
  showNetNewServicingProviders: boolean;
  showZipPanel: boolean;
}

interface AdequacyDetailsState {
  comparedRecords: AdequacyDetailWaffleToppings[];
  comparedSpecialtyExpansions: Map<string, AdequacyDetailWaffleToppings[]>;
  comparedRowGroups: Map<string, string[]>;
  isProcessingUpdates: boolean;
  resultsView: ResultsView;
  records: AdequacyDetailWaffleToppings[];
  rowGroups: Map<string, string[]>;
  selectedRecords: AdequacyDetail[];
  specialtyExpansions: Map<string, AdequacyDetailWaffleToppings[]>;
  templateCategories: TemplateCategory[];
  hasNetNewServicingProviders: boolean;
}

class AdequacyDetails extends Component<AdequacyDetailsProps, AdequacyDetailsState> {
  private projectService = new ProjectService();
  private templateService = new TemplateService();
  private netNewServicingProviderService = new NetNewServicingProviderService();
  constructor(props: AdequacyDetailsProps) {
    super(props);
    this.state = {
      comparedRecords: props.compareToRunConfiguration && this.props.compareToAdequacyResult ? this.buildWaffleToppings(props.compareToAdequacyResult.details, true) : [],
      comparedRowGroups: null,
      comparedSpecialtyExpansions: new Map<string, AdequacyDetailWaffleToppings[]>(),
      hasNetNewServicingProviders: false,
      isProcessingUpdates: true,
      records: this.buildWaffleToppings(sortBy(this.props.adequacyResult.details, ["defaultDisplayOrder"])),
      resultsView: {
        showMarket: false,
        showNetNewServicingProviders: false,
        showNetwork: false,
        showNetworkComparison: false,
        showNextClosestMarketProviders: false,
        showNextClosestNetworkProviders: false,
        showNextClosestProspectProviders: false,
        showPatternOfCareNetworkProviders: false,
        showProspect: false,
        showZipPanel: false,
      },
      rowGroups: new Map<string, string[]>(),
      selectedRecords: [],
      specialtyExpansions: new Map<string, AdequacyDetailWaffleToppings[]>(),
      templateCategories: [],
    };
  }

  public async componentDidMount() {
    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }

    const templateCategories = await this.templateService.getTemplateCategories(this.props.templateMetadata.templateCollectionId, this.props.templateMetadata.templateId);

    let newRecords = this.buildWaffleToppings(this.props.adequacyResult.details, false);
    let comparedRecords = this.buildWaffleToppings((this.props.compareToAdequacyResult && this.props.compareToAdequacyResult.details) || [], true);
    [newRecords, comparedRecords] = this.reconcileComparedDifferences(newRecords, comparedRecords);

    this.setState({
      comparedRecords,
      isProcessingUpdates: false,
      records: newRecords,
      templateCategories,
    });
  }

  public componentDidUpdate(prevProps: AdequacyDetailsProps) {
    let shouldGetNewResultsView = false;
    if (
      (!isEqual(prevProps.adequacyResult.details, this.props.adequacyResult.details) ||
        (!isEqual(prevProps.compareToRunConfiguration, this.props.compareToRunConfiguration))) ||
      !isEqual(prevProps.project, this.props.project) ||
      !isEqual(prevProps.adequacyResult.adequacyResultTypeText, this.props.adequacyResult.adequacyResultTypeText) ||
      (prevProps.cmsCreditIsApplying !== this.props.cmsCreditIsApplying ||
        prevProps.cmsCreditShowOnlyChanges !== this.props.cmsCreditShowOnlyChanges) ||
      (prevProps.runConfiguration?.ignoreTimeAndDistanceWithWithoutValues !== this.props.runConfiguration?.ignoreTimeAndDistanceWithWithoutValues) ||
      (prevProps.module === 'Mapping' && prevProps.module !== this.props.module)
    ) {
      shouldGetNewResultsView = true;
      this.setState({ isProcessingUpdates: true }, () => {
        const records = this.buildWaffleToppings(sortBy(this.props.adequacyResult.details, ["defaultDisplayOrder"]));
        const freshExpansions = new Map<string, AdequacyDetailWaffleToppings[]>();
        this.props.adequacyResult.details.filter((d) => d.canExpandSpecialties).forEach((f) => freshExpansions.set(f.key, []));
        const populatedSpecialtyExpansions = new Map<string, AdequacyDetailWaffleToppings[]>(freshExpansions);
        const rowGroups = this.buildRowGroups(this.props.adequacyResult.details);
        const comparedRowGroups = this.props.compareToAdequacyResult ? this.buildRowGroups(this.props.compareToAdequacyResult.details) : null;

        this.setState({
          comparedRecords: [],
          comparedRowGroups,
          isProcessingUpdates: false,
          records,
          rowGroups,
          specialtyExpansions: populatedSpecialtyExpansions,
        });
      });
    }

    if (this.props.runConfiguration &&
      prevProps.runConfiguration?.projectRunConfigurationId !== this.props.runConfiguration.projectRunConfigurationId) {
      shouldGetNewResultsView = true;
    }

    if ((this.props.compareToRunConfiguration &&
      this.props.compareToAdequacyResult &&
      (!prevProps.compareToAdequacyResult ||
        !isEqual(prevProps.adequacyResult.details, this.props.adequacyResult.details) ||
        !isEqual(prevProps.compareToAdequacyResult.details, this.props.compareToAdequacyResult.details) ||
        !isEqual(prevProps.compareToRunConfiguration, this.props.compareToRunConfiguration)))
      || (prevProps.adequacyFilters !== this.props.adequacyFilters)) {
      this.setState({ isProcessingUpdates: true }, () => {
        let newRecords = this.buildWaffleToppings(this.props.adequacyResult.details, false);
        let comparedRecords = this.buildWaffleToppings(this.props.compareToAdequacyResult.details, true);
        [newRecords, comparedRecords] = this.reconcileComparedDifferences(newRecords, comparedRecords);
        const freshComparedSpecialtyExpansions = new Map<string, AdequacyDetailWaffleToppings[]>();
        this.props.compareToAdequacyResult.details.filter((r) => r.canExpandSpecialties).forEach((r) => freshComparedSpecialtyExpansions.set(r.key, []));
        const populatedComparedSpecialtyExpansions = new Map<string, AdequacyDetailWaffleToppings[]>(freshComparedSpecialtyExpansions);

        this.setState({
          comparedRecords,
          comparedRowGroups: this.props.compareToAdequacyResult ? this.buildRowGroups(this.props.compareToAdequacyResult.details) : null,
          comparedSpecialtyExpansions: populatedComparedSpecialtyExpansions,
          isProcessingUpdates: false,
          records: newRecords,
        });
      });
    }

    // Wipe the selections on filter change, to avoid referencing now-missing record
    if (!isEqual(prevProps.regionFilters, this.props.regionFilters) || !isEqual(prevProps.filters, this.props.filters) || prevProps.hasProspectOrMarketProviders !== this.props.hasProspectOrMarketProviders) {
      shouldGetNewResultsView = true;
      this.setState({
        selectedRecords: [],
      });
    }
    if (shouldGetNewResultsView) {
      this.getResultsView(this.props.adequacyResult.details[0], !!this.props.compareToRunConfiguration).then((newResultsView) => {
        this.setState({
          isProcessingUpdates: false,
          resultsView: newResultsView,
        }, this.getViewableFields);
      });
    }
  }

  public render() {
    const isComparingResults = !!this.props.compareToRunConfiguration;
    const hasFailed = this.props.runConfiguration && this.props.runConfiguration.status === ProjectRunConfigurationStatus.Failed;

    let haveResultsToRender = ((!this.props.loading &&
      !this.props.loadingResults && !this.state.isProcessingUpdates) ||
      this.props.project.isReadOnly) &&
      !!this.props.runConfiguration &&
      !hasFailed;

    haveResultsToRender = !isComparingResults
      ? haveResultsToRender
      : haveResultsToRender && !!this.props.compareToAdequacyResult;

    let countyRegionCode: string = null;
    let specialtyGroupCode: string = null;
    let specialtyGroupDisplayName: string = null;
    let stateRegionCode: string = null;

    if (this.props.regionFilters && haveResultsToRender) {
      countyRegionCode = this.props.regionFilters.county;
      specialtyGroupCode = this.props.regionFilters.specialty;
      if (!!specialtyGroupCode) {
        let specialtyGroup = this.props.runConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
        if (!specialtyGroup && this.props.compareToRunConfiguration) {
          specialtyGroup = this.props.compareToRunConfiguration.specialtyGroups.find((sg) => sg.code === specialtyGroupCode);
        }
        specialtyGroupDisplayName = specialtyGroup ? `${specialtyGroup.code} – ${specialtyGroup.name}` : "";
      }
      stateRegionCode = this.props.regionFilters.state;
    }

    const isLowestGranularity = !!countyRegionCode && !!specialtyGroupCode;
    return (
      <Fragment>
        {haveResultsToRender ?
          this.renderResults(isComparingResults, isLowestGranularity, countyRegionCode, specialtyGroupCode, specialtyGroupDisplayName, stateRegionCode) :
          <SkeletonResults isLowestGranularity={isLowestGranularity} runConfiguration={this.props.runConfiguration || this.props.compareToRunConfiguration} shimmer={!hasFailed} />
        }
      </Fragment>
    );
  }

  private getRecordCssDecorator = (record: AdequacyDetailWaffleToppings, isNewerRow: boolean): string => {
    const runConfigurationDate = moment(this.props.runConfiguration.dateCreated);
    const compareToRunConfigurationDate = this.props.compareToRunConfiguration ? moment(this.props.compareToRunConfiguration.dateCreated) : null;
    const isCompareOlder = this.props.compareToRunConfiguration ? compareToRunConfigurationDate.isBefore(runConfigurationDate) : false;
    let detail: AdequacyDetail;
    if (!!this.props.compareToRunConfiguration) {
      if ((isCompareOlder && isNewerRow) || (!isCompareOlder && !isNewerRow)) {
        detail = this.props.adequacyResult.details.find((d) => d.key === record.key);
      } else if ((!isCompareOlder && isNewerRow) || (isCompareOlder && !isNewerRow)) {
        detail = this.props.compareToAdequacyResult.details.find((d) => d.key === record.key);
      }
    } else {
      detail = this.props.adequacyResult.details.find((d) => d.key === record.key);
    }
    if (!detail) {
      return "gray danger";
    }
    const exceptionApproved = detail.exception &&
      detail.exception.specialtyGroupExceptionStatus === SpecialtyGroupExceptionStatus.Approved
      && (!detail.accessSummary.metCriteria || !detail.servicingSummary.metCriteria); // only consider approved exceptions if there's currently a gap

    const isDrilledDown = this.props.filters.some((item) => item.filterType === FilterType.County) && this.props.filters.some((item) => item.filterType === FilterType.Specialty);

    const isInformational = detail.specialtyGroupIsInformational ? "muted " : "";
    if (exceptionApproved && !isDrilledDown) {
      return `${isInformational}info`;
    } else if (detail.accessSummary.memberCount === 0) {
      return `${isInformational}gray success`;
    }
    else if (detail.metCriteria || (detail.applicationCreditMetCriteria && this.props.cmsCreditIsApplying)) {
      return `${isInformational}success`;
    }
    else if (
      (detail.accessSummary.metCriteria
        || (this.props.cmsCreditIsApplying && detail.accessSummary.applicationCreditMetCriteria))
      || detail.servicingSummary.metCriteria
    ) {
      return `${isInformational}warning`;
    } else {
      return `${isInformational}danger`;
    }
  }

  private renderAdequacyDetails(bulkActionBar: JSX.Element) {
    const selectedKeys: string[] = [...new Set(this.state.selectedRecords.map((sr: AdequacyDetail) => sr.key))];

    const runConfigurationDate = moment(this.props.runConfiguration.dateCreated);
    const compareToRunConfigurationDate = this.props.compareToRunConfiguration ? moment(this.props.compareToRunConfiguration.dateCreated) : null;
    const isCompareOlder = this.props.compareToRunConfiguration ? compareToRunConfigurationDate.isBefore(runConfigurationDate) : false;

    let data;
    let compareData;
    if (this.props.compareToRunConfiguration && isCompareOlder) {
      data = this.state.comparedRecords;
      compareData = this.state.records.length ? this.state.records : null;
    } else if (this.props.compareToRunConfiguration && !isCompareOlder && this.state.comparedRecords) {
      data = this.state.records;
      compareData = this.state.comparedRecords.length ? this.state.comparedRecords : null;
    } else {
      data = this.state.records;
      compareData = null;
    }

    return (
      <Waffles<AdequacyDetailWaffleToppings>
        bulkActionBar={bulkActionBar}
        columnGroups={this.getColumnGroups()}
        columns={this.getColumnConfig()}
        compareData={compareData && compareData.length ? compareData : null}
        compareExpansions={this.props.compareToRunConfiguration ? isCompareOlder ? this.state.specialtyExpansions : this.state.comparedSpecialtyExpansions : null}
        compareModeKeyColumns={[ProjectPerspective.BySpecialty, ProjectPerspective.All].includes(this.props.perspective) ? 0 : 1}
        compareRowGroups={this.props.compareToRunConfiguration && isCompareOlder ? this.state.rowGroups : this.state.comparedRowGroups}
        customSort={this.customSort}
        data={data || []}
        dataExpansions={this.props.compareToRunConfiguration ? isCompareOlder ? this.state.comparedSpecialtyExpansions : this.state.specialtyExpansions : this.state.specialtyExpansions}
        dataExpansionsDefaultOpen={false}
        dataExpansionOnToggleClick={async (expansionKey: string, includeCompared: boolean) => {
          this.procureNewExpansionData(
            expansionKey,
            includeCompared,
          );
          return;
        }}
        fullWidth={true}
        handleCheckboxChange={this.handleCheckboxChange}
        loading={this.props.loading}
        //onFilterChange={this.filterRecords}
        rowDecorator={this.getRecordCssDecorator}
        rowGroups={this.props.compareToRunConfiguration && isCompareOlder ? this.state.comparedRowGroups : this.state.rowGroups}
        selectedKeys={selectedKeys}
        showCheckboxes={true}
        testId="adequacy-details"
      />
    );
  }

  private handleCheckboxChange = (selectedKeys: string[]) => {
    let data = [...this.props.adequacyResult.details];
    if (!!this.props.compareToRunConfiguration) {
      data = data.concat(this.props.compareToAdequacyResult.details);
    }

    const selectedData = data.filter((result) => selectedKeys.includes(`${result.key}`));
    this.setState({
      selectedRecords: selectedData,
    });
  }

  private getBulkActionBar = (countyRegion: ResultRegion, stateRegion: ResultRegion, specialtyGroupCode: string, setActiveExportType?: ExportType) => {
    // values needed for export properties
    const countyRegionCode = countyRegion ? countyRegion.regionCode : null;

    let networkTypes = this.props.runConfiguration && Array.from(this.props.runConfiguration.networkTypes) || undefined;
    //remove market from available network types if project doesn't used market for impact
    if (this.props.runConfiguration && !this.props.runConfiguration.useMarketProvidersForImpact) {
      networkTypes = networkTypes.filter((nt) => nt !== "market");
    }

    const stateRegionCode = stateRegion ? stateRegion.regionCode : null;
    const granularity = stateRegionCode || countyRegionCode ? RegionGranularity.County : RegionGranularity.State;

    let disableRegionFilters = false;
    let regionSetIds = this.props.perspective !== ProjectPerspective.BySpecialty
      ? uniq(this.state.selectedRecords.map((r) => r.regionSetId))
      : [];
    // if no records are selected, use either the current state or county region set id as the "default" selection for export properties
    if (regionSetIds.length === 0) {
      if (countyRegion) {
        regionSetIds = [countyRegion.regionSetId];
        disableRegionFilters = true;
      } else if (stateRegion) {
        regionSetIds = [stateRegion.regionSetId];
      }
    } else {
      disableRegionFilters = true;
    }

    let disableSpecialtyGroupFilters = false;
    let specialtyGroupCodes = [ProjectPerspective.BySpecialty, ProjectPerspective.All].includes(this.props.perspective)
      ? this.state.selectedRecords.map((r) => r.specialtyGroupCode).filter((s) => !!s)
      : [];
    if (specialtyGroupCodes.length === 0) {
      if (specialtyGroupCode) {
        specialtyGroupCodes = [specialtyGroupCode];
      }
    }
    if (specialtyGroupCodes.length > 0) {
      disableSpecialtyGroupFilters = true;
    }

    let activeExportType: ExportType = null;
    // change the set of available export options based on whether we are showing "by specialty" or "by region" results
    const availableExportTypes = this.props.perspective === ProjectPerspective.BySpecialty ?
      [ExportType.AllSpecialties, ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders] :
      [ExportType.RegionAggregate, ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders];

    if (this.props.runConfiguration.hasZipSummaries || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.hasZipSummaries)) {
      availableExportTypes.push(ExportType.ZipSummary);
    }

    if (this.props.project.showClosestProviderReport && !this.props.compareToRunConfiguration) {
      availableExportTypes.push(ExportType.ClosestProvider);
    }

    if (this.props.perspective === ProjectPerspective.All && this.props.regionFilters.county && this.props.regionFilters.specialty) {
      activeExportType = ExportType.SpecialtySummaryByRegion;
    }

    if (setActiveExportType) {
      activeExportType = setActiveExportType;
    }

    let defaultReportType: ReportType;
    if (!!setActiveExportType && exportTypeToReportTypeMap.has(setActiveExportType))
      defaultReportType = exportTypeToReportTypeMap.get(setActiveExportType);
    else if (this.props.perspective === ProjectPerspective.ByCounty)
      defaultReportType = ReportType.CountySummary;
    else if (this.props.perspective === ProjectPerspective.BySpecialty)
      defaultReportType = ReportType.AdequacyAggregate;
    // drilled in view
    else
      defaultReportType = ReportType.SpecialtySummaryByCounty;

    const showNextClosestProviders = this.props.runConfiguration && this.props.runConfiguration.showNextClosestNetworkProviders && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed && this.props.runConfiguration.networkAssessmentAnalysisType !== NetworkAssessmentAnalysisType.Opportunity || false;

    const { cmsCreditIsApplying, cmsCreditShowOnlyChanges, cmsCreditTemplateValue, setCMSCreditIsApplying, setCMSCreditShowOnlyChanges } = this.props;

    const testId = activeExportType === ExportType.ZipSummary ? `${ExportType.ZipSummary}__create-report-button` : "adequacy-details__create-report-button";

    const showExpandableSpecialties = this.props.adequacyResult.details.filter((d) => d.canExpandSpecialties)?.length > 0;

    const hasPublishedTimeslice: ProjectRunConfiguration =
      this.props.project.projectRunConfigurations?.find((rc) => rc.isLatestPublished && !rc.isProjected && !rc.isTermed && !rc.isProjectedTermed);

    const showMappingButton = this.props.enabledFeatureFlags.has(Features.PROVIDER_MEMBER_MAPS) &&
      !!hasPublishedTimeslice &&
      !!this.props.runConfiguration.datePublished &&
      !this.props.runConfiguration.isProjected &&
      !this.props.runConfiguration.isProjectedTermed &&
      !this.props.runConfiguration.isTermed &&
      !!specialtyGroupCode &&
      !!countyRegionCode &&
      !this.props.compareToRunConfiguration &&
      !this.props.isDualMembershipMode &&
      (
        !this.props.location?.state?.runConfiguration?.projectRunConfigurationType ||
        this.props.location?.state?.runConfiguration?.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental
      );

    // we can't use this.state.resultsView for this because that relies on being at lowest granularity
    const showPatternOfCareCheckbox = showPatternOfCare(this.props.user, this.props.enabledFeatureFlags, !!this.props.compareToRunConfiguration, this.props.project, this.props.runConfiguration, this.props.adequacyResult.details, undefined, this.props.cmsCreditIsApplying);

    const selectedMemberships: number[] = [this.props.runConfiguration.memberSetId];
    if (this.props.isDualMembershipMode) {
      selectedMemberships.push(this.props.compareToRunConfiguration.memberSetId);
    }

    return (
      <span className="bulk-action-group">
        <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
          to={{
            pathname: this.props.exportEndpoint,
            state: {
              activeExportType,
              activeModule: this.props.module,
              adequacyFilters: this.props.adequacyFilters,
              availableExportTypes,
              comparedRunConfigurationId: this.props.exportComparedRunConfigurationId,
              disableRegionFilters,
              disableSpecialtyGroupFilters,
              enableMembershipTypeOptions:
                this.props.enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT) &&
                this.props.runConfiguration &&
                [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental].includes(this.props.runConfiguration.projectRunConfigurationType),
              filters: this.props.filters,
              granularity,
              initialNetworkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              networkTypes: networkTypes as ProviderNetworkType[],
              nextClosestProviders: showNextClosestProviders,
              projectResourceId: this.props.project.resource.resourceId,
              projectResourceName: this.props.project.resource.name,
              projectURL: this.props.location.pathname + this.props.location.search,
              regionSetIds,
              showAdvancedComparison: this.props.project.showAdvancedComparison,
              showAppendNextClosestProviders: showNextClosestProviders,
              showAppendPatternOfCareProviders: showPatternOfCareCheckbox,
              showExpandableSpecialties: showExpandableSpecialties,
              specialtyGroupCodes,

              // new reportcustomization
              /* eslint-disable sort-keys */
              projectName: this.props.project.resource.name,
              networkAssessmentAnalysisType: this.props.networkAssessmentAnalysisType,
              selectedRegionSetIds: !!this.props.regionFilters.county && !!this.props.regionFilters.specialty ? [this.props.adequacyResult.details[0].regionSetId] : this.state.selectedRecords.map((sr) => sr?.regionSetId),
              selectedSpecialties: !!this.props.regionFilters.specialty ? [this.props.regionFilters.specialty] : this.state.selectedRecords.map((sr) => sr?.specialtyGroupCode),
              defaultReportType,
              primaryMembership: { alias: this.props.project.memberSetAlias || this.props.project.memberSetName, id: this.props.project.memberSetId },
              secondaryMembership: { alias: this.props.project.memberSetAlias2 || this.props.project.memberSetName2, id: this.props.project.memberSetId2 },
              selectedMemberships,
              selectedTimeslice: this.props.runConfiguration.projectRunConfigurationId, // runConfigurationId
              isCompareMode: !!this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
              selectedComparedTimeslice: !this.props.isDualMembershipMode ? this.props.compareToRunConfiguration?.projectRunConfigurationId : null, // compareToRunConfigurationId
              isViewingOpportunity: this.props.adequacyFilters.opportunity,
              showAddedProviders: !!this.props.compareToRunConfiguration,
              showMatchedProviders: !!this.props.compareToRunConfiguration,
              showRemovedProviders: !!this.props.compareToRunConfiguration,
              showPatternOfCare: this.props.project.showPatternOfCareProviders,
              hasProspectProviders: !!this.props.runConfiguration.prospectProviderSetId,
              hasMarketProviders: !!this.props.runConfiguration.marketProviderSetId,
              hasNextClosestProviders: !!this.props.runConfiguration.showNextClosestNetworkProviders,
              showZipSummariesReport: this.props.project.hasZipSummaries,
              /* eslint-enable */
            },
          }}
          data-testid={testId}
        >
          <i className="fa fa-cloud-download" /> Create Report
        </Link>
        {this.buildMappingLink(this.props, showMappingButton)}
        {!this.props.compareToRunConfiguration && this.props.project.showCMSCredit && cmsCreditIconToggleApply(cmsCreditIsApplying, setCMSCreditIsApplying, cmsCreditTemplateValue)}
        {!this.props.compareToRunConfiguration && this.props.project.showCMSCredit && cmsCreditIconToggleChanges(cmsCreditIsApplying, cmsCreditShowOnlyChanges, setCMSCreditShowOnlyChanges, cmsCreditTemplateValue, this.props.adequacyFilters.gaps, (Boolean)(this.props.regionFilters.county) && (Boolean)(this.props.regionFilters.specialty))}
      </span>
    );
  }

  private customSort = (records: AdequacyDetailWaffleToppings[], column: keyof AdequacyDetailWaffleToppings, direction: string) => {
    const data = [...records];
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "title":
        return data.sort(
          (a, b) => {
            return (a[column].key > b[column].key ? 1 : -1) * multiplier;
          });
      case "accessMetDist":
      case "accessMetTime":
      case "accessMet":
      case "servicingMet":
      case "overallMet":
      case "countyClass":
      case "fillableGapIndicator":
      case "gapFillStatus":
      case "gapFillStatusUpdatedByName":
        return data.sort(
          (a, b) => {
            return (a[column] > b[column] ? 1 : -1) * multiplier;
          });
      case "gapFillStatusUpdatedDate":
        return sortByDate(data, "gapFillStatusUpdatedDate", direction, "key");
      case "countySSA":
      case "accessWith":
      case "accessAvgDist2With":
      case "accessAvgDist2Without":
      case "accessAvgDistWith":
      case "accessAvgDistWithout":
      case "accessAvgTime2With":
      case "accessAvgTime2Without":
      case "accessAvgTimeWith":
      case "accessAvgTimeWithout":
      case "accessMaxDistWith":
      case "accessMaxDistWithout":
      case "accessMaxTimeWith":
      case "accessMaxTimeWithout":
      case "accessWithout":
      case "accessAvgDist":
      case "accessAvgDist2":
      case "accessWithDist":
      case "accessWithoutDist":
      case "accessMaxDist":
      case "accessAvgTime":
      case "accessAvgTime2":
      case "accessWithTime":
      case "accessWithoutTime":
      case "accessMaxTime":
      case "servicingRequired":
      case "servicingProviderCount":
      case "servicingDistinct":
      case "overallGapAge":
      case "marketAccess":
      case "marketProviderCount":
      case "marketDistinct":
      case "marketPercentage":
      case "maxReqdDistance":
      case "maxReqdTime":
      case "attestationPercent":
      case "accessTelehealthCredit":
      case "conAccessCreditPercentage":
      case "fullMembershipCount":
      case "fullMemberCountWithAccess":
      case "fullMemberCountWithoutAccess":
        return numberSort(records, column, multiplier);
      case "prospectImpact":
        return data.sort((a, b) => (a[column].key > b[column].key ? 1 : -1) * multiplier);
      default:
        console.warn("No sort definition for that key.");
        return records;
    }
  }

  private shouldShowExceptionHistory = (specialtyGroupException: SpecialtyGroupException) => {
    return specialtyGroupException
      && specialtyGroupException.specialtyGroupExceptionStatus
      && specialtyGroupException.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.None
      && specialtyGroupException.specialtyGroupExceptionStatus !== SpecialtyGroupExceptionStatus.Archived;
  }

  private renderResults = (isComparingResults: boolean, isLowestGranularity: boolean, countyRegionCode: string, specialtyGroupCode: string, specialtyGroupDisplayName: string, stateRegionCode: string) => {
    let county = this.props.runConfiguration.getCounty(countyRegionCode);
    county = !county && isComparingResults
      ? this.props.compareToRunConfiguration.getCounty(countyRegionCode)
      : county;

    let state = this.props.runConfiguration.getState(stateRegionCode);
    state = !state && isComparingResults
      ? this.props.compareToRunConfiguration.getState(stateRegionCode)
      : state;

    let record = isLowestGranularity
      ? this.props.adequacyResult.details[0]
      : null;

    record = !isComparingResults
      ? record
      : !record && isLowestGranularity
        ? this.props.compareToAdequacyResult.details[0]
        : record;

    const accessSummary = isLowestGranularity && record
      ? record.accessSummary
      : null;
    const exception = isLowestGranularity && record
      ? record.exception
      : null;

    const config = this.props.runConfiguration;
    const compareToConfig = this.props.compareToRunConfiguration;
    const comparedIsNewer = compareToConfig && moment(compareToConfig.dateCreated).isAfter(config.dateCreated);
    const compareToZipSummaries = this.props.compareToAdequacyResult &&
      this.props.compareToAdequacyResult.details.length > 0 ?
      this.props.compareToAdequacyResult.details[0].zipSummaries
      : undefined;

    const {
      showNetwork,
      showProspect,
      showMarket,
      showNextClosestNetworkProviders,
      showNextClosestProspectProviders,
      showNextClosestMarketProviders,
      showNetNewServicingProviders,
      showNetworkComparison,
      showPatternOfCareNetworkProviders,
      showZipPanel
    } = this.state.resultsView;

    const mainBulkActionBar = this.getBulkActionBar(county, state, specialtyGroupCode);
    const zipBulkActionBar = this.getBulkActionBar(county, state, specialtyGroupCode, ExportType.ZipSummary);
    const isEditable = this.props.runConfiguration.isLatestPublished && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isTermed && !this.props.runConfiguration.isProjectedTermed && !this.props.project.isEditRestricted;

    return (
      <>
        {this.props.cmsCreditIsApplying && cmsCreditDataAlert(this.props.cmsCreditTemplateValue)}
        {this.renderAdequacyDetails(mainBulkActionBar)}

        <div>
          {((record && !isComparingResults) || null) && (
            <SpecialtyGroupExceptionEditor
              editEnabled={isEditable}
              region={county}
              specialtyGroupCode={specialtyGroupCode}
              specialtyGroupName={specialtyGroupDisplayName}
              exception={exception}
              onSubmit={this.props.setException}
            />
          )}

          {this.props.enabledFeatureFlags.has(Features.GAP_FILL_STATUS) &&
            ((record && !isComparingResults && isEditable) || null) && (
              <GapFillStatusEditor
                customProjectId={this.props.project.projectId}
                projectRunConfigurationId={this.props.projectRunConfigurationId}
                record={record}
                clearCacheImpactAnalysisRunConfigurations={this.props.clearCachedImpactAnalysisRunConfigurations}
                gapFillDescriptionMap={this.props.gapFillStatusDescriptionMap}
                exception={exception}
                exceptionIsEditable={isEditable}
                region={county}
                specialtyGroupName={specialtyGroupDisplayName}
                specialtyGroupCode={specialtyGroupCode}
                onSubmitException={this.props.setException}
              />
            )
          }
        </div>

        <AdequacyDetailInfoTemplate footnote={record?.footnote} cmsCreditIsApplying={this.props.cmsCreditIsApplying} />

        {isLowestGranularity && (
          <>
            {/* Since showZipPanel only is true when drilled in, we only have one AdequacyDetail. So [0]. */}
            {showZipPanel && (
              <ZipSummariesPanel
                bulkActionBar={zipBulkActionBar}
                closestProviderCount={this.props.templateMetadata.closestProviderCount}
                compareToZipSummaries={(comparedIsNewer || !compareToConfig) ?
                  compareToZipSummaries : this.props.adequacyResult.details[0].zipSummaries
                }
                loading={this.props.loading || this.props.loadingResults}
                resultsForComparison={!!compareToConfig && this.getResultsFor(comparedIsNewer)}
                resultsForSource={this.getResultsFor(!comparedIsNewer && !!compareToConfig)}
                showAvgDist={(this.props.runConfiguration.showDistance || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showDistance)) && this.props.project.showAverageDistance}
                showAvgTime={(this.props.runConfiguration.showTime || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showTime)) && this.props.project.showAverageTime}
                showWithAndWithoutTimeDistanceValues={!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues}
                isTimeOnlyCalculationMethod={this.props.adequacyResult.details[0]?.accessSummary.calculationMethod.toString() === CalculationMethodEnum.Time.toLowerCase()}
                isDistanceOnlyCalculationMethod={this.props.adequacyResult.details[0]?.accessSummary.calculationMethod.toString() === CalculationMethodEnum.Distance.toLowerCase()}
                templateMetadata={this.props.templateMetadata}
                zipSummaries={(comparedIsNewer || !compareToConfig) ?
                  (this.props.adequacyResult.details[0]?.zipSummaries || []) :
                  (compareToZipSummaries || [])}
              />
            )}
            {showNetwork && (
              <NetworkProvidersPanel {...this.props} accessSummary={accessSummary} nextClosestProviders={false} projectURL={this.props.location.pathname + this.props.location.search} regionSetId={county.regionSetId} specialtyGroupCode={specialtyGroupCode} showPatternOfCareNetworkProviders={this.state.resultsView.showPatternOfCareNetworkProviders} />
            )}
            {showNextClosestNetworkProviders && (
              <NetworkProvidersPanel {...this.props} accessSummary={accessSummary} nextClosestProviders={true} projectURL={this.props.location.pathname + this.props.location.search} regionSetId={county.regionSetId} specialtyGroupCode={specialtyGroupCode} showPatternOfCareNetworkProviders={false} />
            )}
            {showPatternOfCareNetworkProviders && (
              <PatternOfCareProvidersPanel exportEndpoint={this.props.exportEndpoint} />
            )}
            {this.props.cmsCreditIsApplying && (showProspect || showMarket) && cmsCreditProvidersAlert(this.props.cmsCreditTemplateValue)}
            {!this.props.cmsCreditIsApplying && (
              <>
                {showProspect && (
                  <ImpactProvidersPanel {...this.props} accessSummary={accessSummary} projectURL={this.props.location.pathname + this.props.location.search} providerNetworkType={ProviderNetworkType.Prospect} regionSetId={county.regionSetId} specialtyGroupCode={specialtyGroupCode} />
                )}
                {showNextClosestProspectProviders && showProspect && (
                  <NextClosestImpactProvidersPanel
                    viewableFields={this.props.viewableFieldsByTableType.get(TableType.NextClosestProspectProviders)}
                    editRestricted={this.props.project.isEditRestricted}
                    readOnly={this.props.project.isReadOnly}
                    networkAssessmentAnalysisType={this.props.networkAssessmentAnalysisType}
                    specialtyGroupCode={specialtyGroupCode}
                    regionSetId={county?.regionSetId}
                    project={this.props.project}
                    runConfiguration={this.props.runConfiguration}
                    providerType={ProviderNetworkType.Prospect}
                    processProviderChanges={this.props.processProviderChanges}
                    setProjectHasUnprocessedProjectionProviders={this.props.setProjectHasUnprocessedProjectionProviders}
                    setShouldRefreshMarketProviders={this.props.setShouldRefreshMarketProviders}
                    setShouldRefreshProspectProviders={this.props.setShouldRefreshProspectProviders}
                    shouldRefreshMarketProviders={this.props.shouldRefreshMarketProviders}
                    shouldRefreshProspectProviders={this.props.shouldRefreshProspectProviders}
                  />
                )}
                {showMarket && (
                  <ImpactProvidersPanel {...this.props} accessSummary={accessSummary} projectURL={this.props.location.pathname + this.props.location.search} providerNetworkType={ProviderNetworkType.Market} regionSetId={county.regionSetId} specialtyGroupCode={specialtyGroupCode} />
                )}
                {showNextClosestMarketProviders && showMarket && (
                  <NextClosestImpactProvidersPanel
                    viewableFields={this.props.viewableFieldsByTableType.get(TableType.NextClosestMarketProviders)}
                    editRestricted={this.props.project.isEditRestricted}
                    readOnly={this.props.project.isReadOnly}
                    networkAssessmentAnalysisType={this.props.networkAssessmentAnalysisType}
                    specialtyGroupCode={specialtyGroupCode}
                    regionSetId={county?.regionSetId}
                    project={this.props.project}
                    runConfiguration={this.props.runConfiguration}
                    providerType={ProviderNetworkType.Market}
                    processProviderChanges={this.props.processProviderChanges}
                    setProjectHasUnprocessedProjectionProviders={this.props.setProjectHasUnprocessedProjectionProviders}
                    setShouldRefreshMarketProviders={this.props.setShouldRefreshMarketProviders}
                    setShouldRefreshProspectProviders={this.props.setShouldRefreshProspectProviders}
                    shouldRefreshMarketProviders={this.props.shouldRefreshMarketProviders}
                    shouldRefreshProspectProviders={this.props.shouldRefreshProspectProviders}
                  />
                )}
                {showNetNewServicingProviders && (
                  <NetNewServicingProvidersPanel
                    accessSummary={accessSummary}
                    viewableFields={this.props.viewableFieldsByTableType.get(TableType.NetNewProviders)}
                    editRestricted={this.props.project.isEditRestricted}
                    readOnly={this.props.project.isReadOnly}
                    networkAssessmentAnalysisType={this.props.networkAssessmentAnalysisType}
                    specialtyGroupCode={specialtyGroupCode}
                    regionSetId={county?.regionSetId}
                    project={this.props.project}
                    runConfiguration={this.props.runConfiguration}
                    providerType={ProviderNetworkType.NetNew}
                    processProviderChanges={this.props.processProviderChanges}
                    setProjectHasUnprocessedProjectionProviders={this.props.setProjectHasUnprocessedProjectionProviders}
                    exportEndpoint={this.props.exportEndpoint}
                  />
                )}
              </>
            )}
            {showNetworkComparison && (
              <NetworkProvidersComparisonPanel
                exportEndpoint={this.props.exportEndpoint}
                projectURL={this.props.location.pathname + this.props.location.search}
                regionSetId={county.regionSetId} specialtyGroupCode={specialtyGroupCode}
                exportComparedRunConfigurationId={this.props.exportComparedRunConfigurationId}
              />
            )}
          </>
        )}
      </>
    );
  }

  private getResultsView = async (accessSummary: AdequacyDetail, isComparingResults: boolean) => {
    const summary = !!this.props.regionFilters.county && !!this.props.regionFilters.specialty && this.props.adequacyResult.details[0] ? accessSummary : null;
    const isLowestGranularity = !!this.props.regionFilters.county && !!this.props.regionFilters.specialty;

    let record: AdequacyDetail = null;
    if (isLowestGranularity) {
      if (isComparingResults) {
        record = this.props.compareToAdequacyResult.details[0];
      } else {
        record = this.props.adequacyResult.details[0];
      }
    }

    const showZipPanel = !!summary && !!accessSummary && ((this.props.runConfiguration?.hasZipSummaries && this.props.adequacyResult.details.length > 0) || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.hasZipSummaries));

    const showNetwork = !!summary && !isComparingResults;
    const showProspect = !!summary && !isComparingResults && !!this.props.runConfiguration.prospectProviderSetId && !!this.props.runConfiguration.prospectProviderSetVersionId;
    const showMarket = !!summary && !isComparingResults && this.props.runConfiguration.useMarketProvidersForImpact;
    const showNextClosestNetworkProviders = !!summary && !!record &&
      (!summary.metCriteria || !record.servicingSummary?.metCriteria) &&
      !isComparingResults &&
      this.props.runConfiguration.showNextClosestNetworkProviders &&
      !this.props.runConfiguration.isProjected &&
      !this.props.runConfiguration.isProjectedTermed && this.props.runConfiguration.networkAssessmentAnalysisType !== NetworkAssessmentAnalysisType.Opportunity;

    const showNextClosestProspectProviders = showNextClosestNetworkProviders && !this.props.hasProspectOrMarketProviders && this.props.runConfiguration?.prospectProviderSetId && !this.props.adequacyResult.details[0]?.metCriteria && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed;
    const showNextClosestMarketProviders = showNextClosestNetworkProviders && !this.props.hasProspectOrMarketProviders && this.props.runConfiguration?.useMarketProvidersForImpact && !this.props.adequacyResult.details[0]?.metCriteria && !this.props.runConfiguration.isProjected && !this.props.runConfiguration.isProjectedTermed;

    const showNetworkComparison = !!summary && isComparingResults && this.props.project.showAdvancedComparison;

    if (this.props.user.roles.some(r => r === Roles.NET_NEW_PROVIDER_USER)) {
      this.hasNetNewProviders();
    }

    const showNetNewServicingProviders = !isComparingResults && (this.props.runConfiguration && (this.props.runConfiguration.isLatestPublished || !this.props.runConfiguration.datePublished ||
      this.props.runConfiguration.isProjected || this.props.runConfiguration.isProjectedTermed || this.props.runConfiguration.isTermed) &&
      this.state.hasNetNewServicingProviders)
      ? true : false;

    const showPatternOfCareNetworkProvidersAccordion = showPatternOfCare(this.props.user, this.props.enabledFeatureFlags, isComparingResults, this.props.project, this.props.runConfiguration, [record], undefined, this.props.cmsCreditIsApplying);

    return {
      showMarket,
      showNetNewServicingProviders,
      showNetwork,
      showNetworkComparison,
      showNextClosestMarketProviders,
      showNextClosestNetworkProviders,
      showNextClosestProspectProviders,
      showPatternOfCareNetworkProviders: showPatternOfCareNetworkProvidersAccordion,
      showProspect,
      showZipPanel,
    };
  }

  private getViewableFields = async () => {
    const tableTypes: TableType[] = [];
    if (this.state.resultsView.showNetwork || this.state.resultsView.showNetworkComparison) { tableTypes.push(TableType.NetworkProviders, TableType.ProviderTerminations); }
    if (this.state.resultsView.showProspect) { tableTypes.push(TableType.ProspectProviders); }
    if (this.state.resultsView.showMarket) { tableTypes.push(TableType.MarketProviders); }
    if (this.state.resultsView.showNextClosestNetworkProviders) { tableTypes.push(TableType.NextClosestNetworkProviders); }
    if (this.state.resultsView.showNextClosestProspectProviders) { tableTypes.push(TableType.NextClosestProspectProviders); }
    if (this.state.resultsView.showNextClosestMarketProviders) { tableTypes.push(TableType.NextClosestMarketProviders); }
    if (this.state.resultsView.showNetNewServicingProviders) { tableTypes.push(TableType.NetNewProviders); }

    // Only fetch new fields if there's a table type we don't have fields for. They only change on run configuration change.
    this.props.getAllViewableFields(tableTypes);
  }

  private procureNewExpansionData = async (key: string, includeCompared: boolean) => {
    const runConfigurationId = this.props.runConfiguration.projectRunConfigurationId;
    const compareToRunConfigurationId = this.props.compareToRunConfiguration?.projectRunConfigurationId;

    const newExpansionsMap: Map<string, AdequacyDetailWaffleToppings[]> = new Map(this.state.specialtyExpansions);
    const newComparedExpansionsMap: Map<string, AdequacyDetailWaffleToppings[]> = new Map(this.state.comparedSpecialtyExpansions);
    const promises = [];
    const updatedIndex = this.props.adequacyResult.details.findIndex((lr) => lr.key === key);
    if (newExpansionsMap.has(key) && newExpansionsMap.get(key).length === 0 && this.props.adequacyResult.details.length > 0) {
      promises.push(this.projectService.getSpecialtyLevelSummaries(
        this.props.project.projectId,
        runConfigurationId,
        this.props.adequacyResult.details[updatedIndex].regionSetId,
        this.props.adequacyResult.details[updatedIndex].specialtyGroupCode,
      ));
    }
    if (includeCompared && newComparedExpansionsMap.has(key) && newComparedExpansionsMap.get(key).length === 0 && this.props.compareToAdequacyResult.details.length > 0) {
      const comparedUpdatedIndex = this.props.compareToAdequacyResult.details.findIndex((lr) => lr.key === key);
      promises.push(this.projectService.getSpecialtyLevelSummaries(
        this.props.project.projectId,
        compareToRunConfigurationId,
        this.props.compareToAdequacyResult.details[comparedUpdatedIndex].regionSetId,
        this.props.compareToAdequacyResult.details[comparedUpdatedIndex].specialtyGroupCode,
      ));
    }

    Promise.all(promises).then((responses) => {
      const [selectedResponse, comparedResponse] = responses;
      selectedResponse.forEach((val) => {
        val.isExpandedSpecialty = true;
        val.footnote = this.props.adequacyResult.details[updatedIndex].footnote;
      });
      comparedResponse?.forEach((val) => {
        val.isExpandedSpecialty = true;
        val.footnote = this.props.adequacyResult.details[updatedIndex].footnote;
      });

      newExpansionsMap.set(key, [...this.buildWaffleToppings((selectedResponse || []), false, true)]);
      if (includeCompared) {
        newComparedExpansionsMap.set(key, [...this.buildWaffleToppings((comparedResponse || []), true, true)]);
      }

      this.setState((prevState) => ({
        ...prevState,
        comparedSpecialtyExpansions: includeCompared ? newComparedExpansionsMap : prevState.comparedSpecialtyExpansions,
        specialtyExpansions: newExpansionsMap,
      }));
    })
  }

  private hasNetNewProviders = () => {
    const countyRegionCode = this.props.regionFilters.county;
    const county = this.props.runConfiguration.getCounty(countyRegionCode);

    this.netNewServicingProviderService.regionSetSpecialtyGroupHasNetNewProvidersAsync(
      this.props.project?.projectId,
      this.props.runConfiguration?.projectRunConfigurationId,
      county?.regionSetId,
      this.props.regionFilters?.specialty,
      this.props.networkAssessmentAnalysisType)
      .then((hasNetNew) => {
        if (hasNetNew !== this.state.hasNetNewServicingProviders) {
          this.setState({
            hasNetNewServicingProviders: hasNetNew
          });
        }
      });
  }

  private buildRowGroups = (sortedRecords: AdequacyDetail[]) => {
    if (this.state.templateCategories.length <= 1 || this.props.perspective !== ProjectPerspective.BySpecialty || !sortedRecords) {
      return null;
    }

    const rowGroups = new Map<string, string[]>();
    const templateCategories = this.state.templateCategories;
    templateCategories.sort((a, b) => a.displayOrder - b.displayOrder);
    templateCategories.forEach((cat, index) => {
      const key = cat.displayName || `Unnamed Category ${index + 1}`;
      rowGroups.set(key, sortedRecords.filter((sr) => cat.specialtyGroupCodes.includes(sr.specialtyGroupCode)).map((filteredRecord) => filteredRecord.key));
    });
    return rowGroups;
  }

  private buildWaffleToppings = (records: AdequacyDetail[], compared?: boolean, expanded?: boolean) => {
    if (records.length === 0) {
      return [];
    }
    // Only show informational specialty groups if
    // One of the specialty groups is marked as such.
    // Otherwise, don't show the indicator, it's a waste of space.
    // NOTE: This is NOT a column as we don't want to deal with its interaction with ellipses.
    const showInformationalIndicator = compared ?
      this.props.compareToAdequacyResult.details.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational) :
      this.props.adequacyResult.details.some(({ specialtyGroupIsInformational }) => !!specialtyGroupIsInformational);

    const showHistoryLink = compared ?
      (this.props.compareToAdequacyResult &&
        this.props.compareToAdequacyResult.adequacyResultTypeText === "Specialty Group" &&
        this.props.compareToAdequacyResult.details.some((detail) => this.shouldShowExceptionHistory(detail.exception))
      ) : (
        this.props.adequacyResult.adequacyResultTypeText === "Specialty Group" &&
        this.props.adequacyResult.details.some((detail) => this.shouldShowExceptionHistory(detail.exception))
      );

    const resultsFor = this.getResultsFor(compared);
    const isDrilledIn = this.props.filters.some((f) => f.filterType === FilterType.County) && this.props.filters.some((f) => f.filterType === FilterType.Specialty);

    /* eslint-disable sort-keys */
    const waffleToppings: AdequacyDetailWaffleToppings[] = records.map((row, index) => {
      const isTimeOnlyCalculationMethod = !!row.accessSummary && row.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Time.toLowerCase());
      const isDistanceOnlyCalculationMethod = !!row.accessSummary && row.accessSummary.calculationMethod.toString() === (CalculationMethodEnum.Distance.toLowerCase());

      const waffleTopping: AdequacyDetailWaffleToppings = {
        key: expanded ? `${row.key}:expanded:${index}` : row.key,
        title: (() => {
          const showExceptionHistory = this.shouldShowExceptionHistory(row.exception);
          if (row.isExpandedSpecialty) {
            return (
              <span key={row.displayText}>
                <InformationalIndicator key="indicator" visible={showInformationalIndicator} isInformational={!!row.specialtyGroupIsInformational} />
                <SpecialtyGroupExceptionTimelineLink key="specialty" visible={showHistoryLink} showHistory={showExceptionHistory} specialtyGroupException={row.exception} />
                <span key="display">{row.displayText}</span>
              </span>
            );
          }
          if (isDrilledIn) {
            return (
              <Fragment key={row.specialtyGroupDisplayName}>
                <InformationalIndicator visible={showInformationalIndicator} isInformational={!!row.specialtyGroupIsInformational} />
                <SpecialtyGroupExceptionTimelineLink visible={showHistoryLink} showHistory={showExceptionHistory} specialtyGroupException={row.exception} />
                {row.specialtyGroupDisplayName}
              </Fragment>
            );
          }
          return (
            <span key={row.displayText}>
              <InformationalIndicator visible={showInformationalIndicator} isInformational={!!row.specialtyGroupIsInformational} />
              <SpecialtyGroupExceptionTimelineLink visible={showHistoryLink} showHistory={showExceptionHistory} specialtyGroupException={row.exception} />
              <Link
                to={{
                  pathname: `${this.props.match.url}${row.linkSuffix}`,
                  search: this.props.location.search,
                }}
              >
                {row.displayText}
              </Link>
            </span>
          );
        })(),
        countyClass: (!row.isExpandedSpecialty && row.footnote.geographyClass) ? row.footnote.geographyClass : "---",
        membershipName: (
          row.accessSummary && !!row.accessSummary.membershipName ? (
            <TooltipWrapper text={`Membership: ${row.accessSummary.membershipName}`}>
              <i className="fa fa-users" />
            </TooltipWrapper>
          ) : null
        ),
        fullMembershipCount: !isNullOrUndefined(row.accessSummary?.fullMemberCount) ? row.accessSummary.fullMemberCount.toString() : "---",
        maxReqdDistance: !isNullOrUndefined(row.accessSummary?.maximumDistanceAllowed) && !row.accessSummary.skipAccessTimeAndDistanceCalculations ? row.accessSummary.maximumDistanceAllowed.toString() : "---",
        maxReqdTime: !isNullOrUndefined(row.accessSummary?.maximumTimeAllowed) && !row.accessSummary.skipAccessTimeAndDistanceCalculations ? row.accessSummary.maximumTimeAllowed.toString() : "---",
        resultsFor,
        countySSA: row.regionCode && row.regionGranularity ? row.regionCode : "---",

        fullMemberCountWithAccess: !isNullOrUndefined(row.accessSummary?.fullMemberCountWithAccess) ? row.accessSummary.fullMemberCountWithAccess.toString() : "---",
        accessWith: (row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessPercentage.toFixed(1)) || "---",
        fullMemberCountWithoutAccess: !isNullOrUndefined(row.accessSummary?.fullMemberCountWithoutAccess) ? row.accessSummary.fullMemberCountWithoutAccess.toString() : "---",
        accessWithout: (row.accessSummary && row.accessSummary.memberCount > 0 && (100 - row.accessSummary.memberAccessPercentage).toFixed(1)) || "---",
        accessAvgDist: (!isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringDistance && row.accessSummary.averageCoveringDistance.toFixed(1)) || "---",
        accessAvgDistWith: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageDistanceWithAccess && row.accessSummary.averageDistanceWithAccess.toFixed(1)) || "---",
        accessAvgDistWithout: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageDistanceWithoutAccess && row.accessSummary.averageDistanceWithoutAccess.toFixed(1)) || "---",
        accessAvgDist2: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringDistance2 !== undefined && row.accessSummary.averageCoveringDistance2.toFixed(1)) || "---",
        accessAvgDist2With: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageDistance2WithAccess && row.accessSummary.averageDistance2WithAccess.toFixed(1)) || "---",
        accessAvgDist2Without: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageDistance2WithoutAccess && row.accessSummary.averageDistance2WithoutAccess.toFixed(1)) || "---",
        accessMaxDist: (!isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.maximumDistance && row.accessSummary.maximumDistance.toFixed(1)) || "---",

        accessMaxDistWith: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.maxDistanceWithAccess && row.accessSummary.maxDistanceWithAccess.toFixed(1)) || "---",
        accessMaxDistWithout: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isTimeOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.maxDistanceWithoutAccess && row.accessSummary.maxDistanceWithoutAccess.toFixed(1)) || "---",

        accessWithDist: (!isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessByDistancePercentage && row.accessSummary.memberAccessByDistancePercentage.toFixed(1)) || "---",
        accessWithoutDist: (!isTimeOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessByDistancePercentage && (100 - row.accessSummary.memberAccessByDistancePercentage).toFixed(1)) || "---",
        accessAvgTime: (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringTime && row.accessSummary.averageCoveringTime.toFixed(1)) || "---",
        accessAvgTimeWith: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageTimeWithAccess && row.accessSummary.averageTimeWithAccess.toFixed(1)) || "---",
        accessAvgTimeWithout: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageTimeWithoutAccess && row.accessSummary.averageTimeWithoutAccess.toFixed(1)) || "---",
        accessAvgTime2: (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.averageCoveringTime2 !== undefined && row.accessSummary.averageCoveringTime2.toFixed(1)) || "---",
        accessAvgTime2With: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageTime2WithAccess && row.accessSummary.averageTime2WithAccess.toFixed(1)) || "---",
        accessAvgTime2Without: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.averageTime2WithoutAccess && row.accessSummary.averageTime2WithoutAccess.toFixed(1)) || "---",

        accessMaxTime: (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.maximumTime !== undefined && row.accessSummary.maximumTime.toFixed(1)) || "---",

        accessMaxTimeWith: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.maxTimeWithAccess && row.accessSummary.maxTimeWithAccess.toFixed(1)) || "---",

        accessMaxTimeWithout: (!this.props.runConfiguration.ignoreTimeAndDistanceWithWithoutValues && !isDistanceOnlyCalculationMethod && row.accessSummary?.memberCount > 0 && row.accessSummary.maxTimeWithoutAccess && row.accessSummary.maxTimeWithoutAccess.toFixed(1)) || "---",

        accessWithTime: (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessByTimePercentage && row.accessSummary.memberAccessByTimePercentage.toFixed(1)) || "---",
        accessWithoutTime: (!isDistanceOnlyCalculationMethod && row.accessSummary && row.accessSummary.memberCount > 0 && row.accessSummary.memberAccessByTimePercentage && (100 - row.accessSummary.memberAccessByTimePercentage).toFixed(1)) || "---",
        accessMetDist: (() => {
          return row.accessSummary && !isTimeOnlyCalculationMethod && row.accessSummary.memberCount > 0
            ? row.accessSummary.memberAccessByDistancePercentage
              ? row.accessSummary.metByDistance
                ? "Y"
                : "N"
              : "---"
            : "---";
        })(),
        accessMetTime: (() => {
          return row.accessSummary && !isDistanceOnlyCalculationMethod && row.accessSummary.memberCount > 0
            ? row.accessSummary.memberAccessByTimePercentage
              ? row.accessSummary.metByTime
                ? "Y"
                : "N"
              : "---"
            : "---";
        })(),
        accessMet: (() => {
          return row.accessSummary && row.accessSummary.memberCount > 0
            ? this.props.cmsCreditIsApplying && row.accessSummary.applicationCreditMetCriteria
              ? "Y"
              : row.accessSummary.metCriteria ? "Y" : "N"
            : "---";
        })(),
        accessTelehealthCredit: row && row.accessTelehealthCredit ? row.accessTelehealthCredit.toFixed(1) : "---",
        conAccessCreditPercentage: row && row.conAccessCreditPercentage ? row.conAccessCreditPercentage.toFixed(1) : "---",
        servicingRequired: (row.servicingSummary ? printNumberAsString(row.servicingSummary.minimumServicingProviders, "---") : "---"),
        servicingProviderCount: (row.servicingSummary ? printNumberAsString(row.servicingSummary.inRegionProviderCount, "---") : "---"),
        servicingDistinct: (row.servicingSummary ? printNumberAsString(row.servicingSummary.distinctServicingProviderCount, "---") : "---"),
        servicingProviderToMemberRatio: row.servicingSummary ? row.servicingSummary.servicingProviderToMemberRatio : "---",
        servicingMet: (row.servicingSummary && (row.servicingSummary.metCriteria ? "Y" : "N")) || "---",
        overallMet: row.accessSummary.memberCount === 0 ? "---" :
          this.props.cmsCreditIsApplying && row.applicationCreditMetCriteria ? "Y" :
            row.metCriteria ? "Y" : "N",
        overallGapAge: printNumberAsString(row.gapAgeDays, "---"),
        marketAccess: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          row.accessSummary.memberCount === 0 ? "---" :
            (row.marketSummary && row.marketSummary.memberAccessPercentage.toFixed(1)) || "---",
        marketProviderCount: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary ? printNumberAsString(row.marketSummary.inRegionProviderCount, "---") : "---"),
        marketDistinct: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary ? printNumberAsString(row.marketSummary.distinctServicingProviderCount, "---") : "---"),
        marketPercentage: row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis ? null :
          (row.marketSummary && row.marketSummary.marketPercentage && row.marketSummary.marketPercentage.toFixed(1)) || "---",
        prospectImpact: !(row.isExpandedSpecialty && row.hideSpecialtyMarketAnalysis) ? getImpactIndicator(row.impactIndicator, row.metCriteria) : <span />,
        attestationPercent: (row.servicingSummary &&
          row.servicingSummary.recentlyAttestedUniqueNPIPercent !== undefined &&
          row.servicingSummary.recentlyAttestedUniqueNPIPercent !== null &&
          row.servicingSummary.recentlyAttestedUniqueNPIPercent.toFixed(1)
        ) ||
          "---",
        fillableGapIndicator: row.fillableGapIndicator,
        gapFillStatus: this.props.gapFillStatusDescriptionMap[row.gapFillStatus] || "---",
        gapFillStatusUpdatedDate: !!row.gapFillStatusUpdatedDate ? formatDateCustom(row.gapFillStatusUpdatedDate, "MMM D 'YY hh:mm A") : '---',
        gapFillStatusUpdatedByName: row.gapFillStatusUpdatedByName ?? '---',
        gapFillStatusComment: row.gapFillStatusComment ?? '---',
      };

      if (row.footnote?.skipAccessTimeAndDistance)
        this.setAdequacyDetailWaffleToppingPropertiesToValue(waffleTopping, this.accessTimeAndDistanceColumns, "---");
      if (row.isExpandedSpecialty && row.suppressSpecialtyAccessResults)
        this.setAdequacyDetailWaffleToppingPropertiesToValue(waffleTopping, this.accessTimeAndDistanceColumns, null);
      if (row.isExpandedSpecialty)
        this.setAdequacyDetailWaffleToppingPropertiesToValue(waffleTopping, this.expandedSpecialtyNullColumns, null);

      return waffleTopping;
    });
    /* eslint-enable sort-keys */
    return waffleToppings;
  }

  private setAdequacyDetailWaffleToppingPropertiesToValue = (waffleTopping: AdequacyDetailWaffleToppings, properties: (keyof AdequacyDetailWaffleToppings)[], value: string): void => {
    for (const property of properties) {
      waffleTopping[property] = (value as string & JSX.Element);
    }
  }

  private accessTimeAndDistanceColumns: (keyof AdequacyDetailWaffleToppings)[] = [
    "fullMemberCountWithAccess",
    "accessWith",
    "fullMemberCountWithoutAccess",
    "accessWithout",
    "accessAvgDist",
    "accessAvgDistWith",
    "accessAvgDistWithout",
    "accessAvgDist2",
    "accessAvgDist2With",
    "accessAvgDist2Without",
    "accessMaxDist",
    "accessMaxDistWith",
    "accessMaxDistWithout",
    "accessWithDist",
    "accessWithoutDist",
    "accessAvgTime",
    "accessAvgTimeWith",
    "accessAvgTimeWithout",
    "accessAvgTime2",
    "accessAvgTime2With",
    "accessAvgTime2Without",
    "accessMaxTime",
    "accessMaxTimeWith",
    "accessMaxTimeWithout",
    "accessWithTime",
    "accessWithoutTime",
    "accessMetDist",
    "accessMetTime",
  ];
  private expandedSpecialtyNullColumns: (keyof AdequacyDetailWaffleToppings)[] = [
    "accessMetDist",
    "accessMetTime",
    "accessMet",
    "servicingRequired",
    "servicingMet",
    "overallMet",
  ];

  private reconcileComparedDifferences = (newRecords: AdequacyDetailWaffleToppings[], comparedRecords: AdequacyDetailWaffleToppings[]) => {
    if (!this.props.compareToRunConfiguration) {
      return [newRecords, []];
    }
    const selectedResultsFor = this.getResultsFor(false);
    const comparedResultsFor = this.getResultsFor(true);
    const getKeys = (records: AdequacyDetailWaffleToppings[]) => {
      return (records || []).reduce((result, current) => {
        result.push(current.key);
        return result;
      }, []);
    };
    const sortResult = (records: AdequacyDetailWaffleToppings[], order: Map<string, number | string>) => {
      return (records || []).sort((a, b) => {
        const aDisplayOrder = order.get(a.key);
        const bDisplayOrder = order.get(b.key);
        return aDisplayOrder > bDisplayOrder ? 1 : -1;
      });
    };
    const selectedKeys = getKeys(newRecords);
    const comparedKeys = getKeys(comparedRecords);
    const combinedKeys = [...selectedKeys.concat(comparedKeys)];
    const defaultOrder = new Map<string, string | number>();

    combinedKeys.forEach((key) => {
      if (!selectedKeys.includes(key)) {
        const blankRecord = new AdequacyDetailWaffleToppings(key, selectedResultsFor);
        const pairedRecord = comparedRecords.find((d) => key === d.key);
        blankRecord.title = pairedRecord.title;
        newRecords.push(blankRecord);

        defaultOrder.set(key, this.props.compareToAdequacyResult.details.find((r) => r.key === key).defaultDisplayOrder);
      }
      if (!comparedKeys.includes(key)) {
        const blankRecord = new AdequacyDetailWaffleToppings(key, comparedResultsFor);
        const pairedRecord = newRecords.find((d) => key === d.key);
        blankRecord.title = pairedRecord.title;
        comparedRecords.push(blankRecord);

        defaultOrder.set(key, this.props.adequacyResult.details.find((r) => r.key === key).defaultDisplayOrder);
      }
      if (!defaultOrder.get(key)) {
        defaultOrder.set(key, this.props.adequacyResult.details.find((r) => r.key === key).defaultDisplayOrder);
      }
    });

    return [sortResult(newRecords, defaultOrder), sortResult(comparedRecords, defaultOrder)];
  }

  private getResultsFor = (comparedConfig: boolean) => {
    let resultsFor: string = null;
    if (this.props.isDualMembershipMode) {
      if (comparedConfig) {
        resultsFor = this.props.project.projectRunConfigurations.find((rc) => rc.projectRunConfigurationId === this.props.runConfiguration.companionProjectRunConfigurationId).memberSetAlias;
      } else {
        resultsFor = this.props.runConfiguration.memberSetAlias;
      }
    } else {
      resultsFor = comparedConfig ? getTimeString(this.props.compareToRunConfiguration, this.props.runFrequency) : getTimeString(this.props.runConfiguration, this.props.runFrequency);
    }
    return resultsFor;
  }

  private getColumnConfig = () => {
    const showAvgDist = (this.props.runConfiguration.showDistance || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showDistance)) && this.props.project.showAverageDistance;
    const showMaxDist = (this.props.runConfiguration.showDistance || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showDistance)) && this.props.project.showMaximumDistance;
    const showAvgTime = (this.props.runConfiguration.showTime || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showTime)) && this.props.project.showAverageTime;
    const showMaxTime = (this.props.runConfiguration.showTime || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showTime)) && this.props.project.showMaximumTime;
    const showWithTimeAndDistanceColumns = this.props.runConfiguration.showWithTimeAndDistanceColumns || (this.props.compareToRunConfiguration && this.props.compareToRunConfiguration.showWithTimeAndDistanceColumns);
    const hideInCompareMode = !!this.props.compareToRunConfiguration;
    const hideTelehealth = this.props.runConfiguration.telehealthSpecialtyGroupCodes.length <= 0 && (!this.props.compareToRunConfiguration || this.props.compareToRunConfiguration.telehealthSpecialtyGroupCodes.length <= 0);
    const hideCon = !this.props.runConfiguration.hasConCredits || (this.props.compareToRunConfiguration && !this.props.compareToRunConfiguration.hasConCredits);
    const hideMembershipInfo = !(this.props.perspective === ProjectPerspective.BySpecialty || this.props.perspective === ProjectPerspective.ByCounty || this.props.perspective === ProjectPerspective.All) || (!this.props.adequacyResult || this.props.adequacyResult.details.every((r) => !r.accessSummary || !r.accessSummary.membershipName));
    const hideGapFillStatus = !this.props.enabledFeatureFlags.has(Features.GAP_FILL_STATUS);

    const showDistanceRequirements = this.isDistancePartOfTheTemplate() && this.props.perspective === ProjectPerspective.BySpecialty;
    const showTimeRequirements = this.isTimePartOfTheTemplate() && this.props.perspective === ProjectPerspective.BySpecialty;

    // The order of these columns must match the order of property assignments in the
    // AdequacyDetailWaffleToppings constructor for compare mode to display properly.
    const columnConfig: ColumnConfig = {
      accessAvgDist: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1" : "Avg Dist",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1" : "Access: Avg Dist",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        testId: "access-avg-dist",
      },
      accessAvgDist2: {
        displayText: "Avg Dist 2",
        filterText: "Access: Avg Dist 2",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-dist-2",
      },
      accessAvgDist2With: {
        displayText: "Avg Dist 2 With Access",
        filterText: "Access: Avg Dist 2 With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-dist2-with",
      },
      accessAvgDist2Without: {
        displayText: "Avg Dist 2 Without Access",
        filterText: "Access: Avg Dist 2 Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-dist2-without",
      },
      accessAvgDistWith: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 With Access" : "Avg Dist With Access",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1 With Access" : "Access: Avg Dist With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        testId: "access-avg-dist-with",
      },
      accessAvgDistWithout: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Dist 1 Without Access" : "Avg Dist Without Access",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Dist 1 Without Access" : "Access: Avg Dist Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgDist,
        testId: "access-avg-dist-without",
      },
      accessAvgTime: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Time 1" : "Avg Time",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1" : "Access: Avg Time",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        testId: "access-avg-time",
      },
      accessAvgTime2: {
        displayText: "Avg Time 2",
        filterText: "Access: Avg Time 2",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-time-2",
      },
      accessAvgTime2With: {
        displayText: "Avg Time 2 With Access",
        filterText: "Access: Avg Time 2 With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-time2-with",
      },
      accessAvgTime2Without: {
        displayText: "Avg Time 2 Without Access",
        filterText: "Access: Avg Time 2 Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime || this.props.templateMetadata.closestProviderCount !== 2,
        testId: "access-avg-time2-without",
      },
      accessAvgTimeWith: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 With Access" : "Avg Time With Access",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1 With Access" : "Access: Avg Time With Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        testId: "access-avg-time-with",
      },
      accessAvgTimeWithout: {
        displayText: this.props.templateMetadata.closestProviderCount > 1 ? "Avg Time 1 Without Access" : "Avg Time Without Access",
        filterText: this.props.templateMetadata.closestProviderCount > 1 ? "Access: Avg Time 1 Without Access" : "Access: Avg Time Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showAvgTime,
        testId: "access-avg-time-without",
      },
      accessMaxDist: {
        displayText: "Max Dist",
        filterText: "Access: Max Dist",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        testId: "access-max-dist",
      },
      accessMaxDistWith: {
        displayText: "Max Dist With Access",
        filterText: "Access: Max Dist With Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        testId: "access-max-dist-with",
      },
      accessMaxDistWithout: {
        displayText: "Max Dist Without Access",
        filterText: "Access: Max Dist Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxDist,
        testId: "access-max-dist-without",
      },
      accessMaxTime: {
        displayText: "Max Time",
        filterText: "Access: Max Time",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        testId: "access-max-time",
      },
      accessMaxTimeWith: {
        displayText: "Max Time With Access",
        filterText: "Access: Max Time With Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        testId: "access-max-time-with",
      },
      accessMaxTimeWithout: {
        displayText: "Max Time Without Access",
        filterText: "Access: Max Time Without Access",
        filterType: WafflesFilterType.number,
        hidden: !showMaxTime,
        testId: "access-max-time-without",
      },
      accessMet: {
        displayText: "Met",
        filterText: "Access: Met",
        filterType: WafflesFilterType.string,
        hidden: showWithTimeAndDistanceColumns,
        testId: "access-met",
      },
      accessMetDist: {
        displayText: "Met Dist",
        filterText: "Access: Met Dist",
        filterType: WafflesFilterType.string,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-met-dist",
      },
      accessMetTime: {
        displayText: "Met Time",
        filterText: "Access: Met Time",
        filterType: WafflesFilterType.string,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-met-time",
      },
      accessTelehealthCredit: {
        displayText: "Telehealth %",
        filterText: "Access: Telehealth %",
        filterType: WafflesFilterType.number,
        hidden: hideTelehealth,
        testId: "telehealth-credit",
      },
      accessWith: {
        displayText: "With %",
        filterText: "Access: With %",
        filterType: WafflesFilterType.number,
        testId: "access-with-percent",
      },
      accessWithDist: {
        displayText: "With Dist %",
        filterText: "Access: With Dist %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-with-dist",
      },
      accessWithTime: {
        displayText: "With Time %",
        filterText: "Access: With Time %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-with-time-percent",
      },
      accessWithout: {
        displayText: "Without %",
        filterText: "Access: Without %",
        filterType: WafflesFilterType.number,
        testId: "access-without-percent",
      },
      accessWithoutDist: {
        displayText: "Without Dist %",
        filterText: "Access: Without Dist %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-without-dist",
      },
      accessWithoutTime: {
        displayText: "Without Time %",
        filterText: "Access: Without Time %",
        filterType: WafflesFilterType.number,
        hidden: !showWithTimeAndDistanceColumns || hideInCompareMode,
        testId: "access-without-time",
      },
      attestationPercent: {
        displayText: "Attestation %",
        filterText: "Attestation %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.project.showAttestation,
        testId: "attestation-percent",
      },
      conAccessCreditPercentage: {
        displayText: "CON %",
        filterText: "Access: CON %",
        filterType: WafflesFilterType.number,
        hidden: hideCon,
        testId: "con-credit",
      },
      countyClass: {
        displayText: "County Class",
        filterText: "County Class",
        filterType: WafflesFilterType.string,
        hidden: hideInCompareMode || !!this.props.regionFilters.county || this.props.adequacyResult.details.some((r) => r.regionCode && r.regionGranularity !== RegionGranularity.County),
        testId: "county-class",
      },
      countySSA: {
        displayText: "County SSA",
        filterText: "County SSA",
        filterType: WafflesFilterType.string,
        hidden: hideInCompareMode || !!this.props.regionFilters.county || this.props.adequacyResult.details.some((r) => r.regionCode && r.regionGranularity === RegionGranularity.County),
        testId: "county-ssa",
      },
      fillableGapIndicator: {
        displayText: "Fillable Gap Indicator",
        hidden: !(this.props.runConfiguration.useMarketProvidersForImpact || !!this.props.runConfiguration.prospectProviderSetId) || this.props.cmsCreditIsApplying,
        noFilter: true,
        testId: "fillable-gap-indicator",
        tooltip: getFillableGapsIndicatorTooltip(this.props.runConfiguration),
      },
      fullMemberCountWithAccess: {
        displayText: "# With ",
        hidden: hideMembershipInfo,
        testId: "membership-count-with-access",
      },
      fullMemberCountWithoutAccess: {
        displayText: "# Without ",
        hidden: hideMembershipInfo,
        testId: "membership-count-without-access",
      },
      fullMembershipCount: {
        displayText: "Membership Count",
        hidden: hideMembershipInfo,
        testId: "membership-count",
      },
      gapFillStatus: {
        displayText: "Status",
        hidden: hideGapFillStatus,
        testId: "gap-fill-status",
      },
      gapFillStatusUpdatedByName: {
        displayText: "Updated By",
        hidden: hideGapFillStatus,
        testId: "gap-fill-status-updated-by-name"
      },
      gapFillStatusUpdatedDate: {
        displayText: "Last Updated",
        hidden: hideGapFillStatus,
        testId: "gap-fill-status-updated-date"
      },
      marketAccess: {
        displayText: "Access %",
        filterText: "Market Analysis: Access %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.runConfiguration.marketProviderSetId,
        testId: "access-percent",
      },
      marketDistinct: {
        displayText: "Servicing",
        filterText: "Market Analysis: Servicing",
        filterType: WafflesFilterType.number,
        hidden: !this.props.runConfiguration.marketProviderSetId,
        testId: "market-distinct",
      },
      marketPercentage: {
        displayText: "Market %",
        filterText: "Market Analysis: Market %",
        filterType: WafflesFilterType.number,
        hidden: !this.props.runConfiguration.marketProviderSetId,
        testId: "market-percentage",
      },
      marketProviderCount: {
        displayText: getServicingGranularityHeader(this.props.runConfiguration.template.servicingAnalysisGranularity),
        filterText: `Market Analysis: ${getServicingGranularityHeader(this.props.runConfiguration.template.servicingAnalysisGranularity)}`,
        filterType: WafflesFilterType.number,
        hidden: !this.props.runConfiguration.marketProviderSetId,
        testId: "market-provider-count",
      },
      maxReqdDistance: {
        displayText: "Reqd Dist",
        hidden: !showDistanceRequirements,
        testId: "max-reqd-distance",
      },
      maxReqdTime: {
        displayText: "Reqd Time",
        hidden: !showTimeRequirements,
        testId: "max-reqd-time",
      },
      membershipName: {
        alignment: TextAlignment.Center,
        displayElement: <i className="fa fa-users" />,
        displayText: "Membership",
        hidden: hideMembershipInfo,
        noFilter: true,
        noSort: true,
        testId: "membershipName",
      },
      overallGapAge: {
        displayText: "Gap Age",
        filterText: "Overall: Gap Age",
        filterType: WafflesFilterType.number,
        hidden: !this.props.project.includeGapAging,
        testId: "overall-gap-age",
      },
      overallMet: {
        displayText: "Met",
        filterText: "Overall: Met",
        filterType: WafflesFilterType.string,
        testId: "overall-met",
      },
      resultsFor: {
        displayText: "Results For",
        hidden: !this.props.compareToRunConfiguration && !this.props.isDualMembershipMode,
        noFilter: true,
        noSort: true,
        sticky: true,
        testId: "results-for",
      },
      servicingDistinct: {
        displayText: "Servicing",
        filterText: "Servicing Provider Requirements: Servicing",
        filterType: WafflesFilterType.number,
        testId: "servicing-distinct",
      },
      servicingMet: {
        displayText: "Met",
        filterText: "Servicing Provider Requirements: Met",
        filterType: WafflesFilterType.string,
        testId: "servicing-met",
      },
      servicingProviderCount: {
        displayText: getServicingGranularityHeader(this.props.runConfiguration.template.servicingAnalysisGranularity),
        filterText: `Servicing Provider Requirements: ${getServicingGranularityHeader(this.props.runConfiguration.template.servicingAnalysisGranularity)}`,
        filterType: WafflesFilterType.number,
        testId: "servicing-provider-count",
      },
      servicingProviderToMemberRatio: {
        displayText: "Svcg Prov To Mbr Ratio",
        filterText: "Servicing Provider Requirements: Svcg Prov To Mbr Ratio",
        filterType: WafflesFilterType.string,
        testId: "servicing-provider-to-member-ratio",
      },
      servicingRequired: {
        displayText: "Required",
        filterText: "Servicing Provider Requirements: Required",
        filterType: WafflesFilterType.number,
        testId: "servicing-required",
      },
      title: {
        displayText: this.props.adequacyResult.adequacyResultTypeText,
        filterType: WafflesFilterType.string,
        sticky: true,
        testId: "title",
      },
    };
    return columnConfig;
  }

  private isDistancePartOfTheTemplate = (): boolean => {
    return this.props.adequacyResult.details.some(detail =>
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.Distance.toLowerCase() ||
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeAndDistance.toLowerCase() ||
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeOrDistance.toLowerCase()
    );
  }

  private isTimePartOfTheTemplate = (): boolean => {
    return this.props.adequacyResult.details.some(detail =>
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.Time.toLowerCase() ||
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeAndDistance.toLowerCase() ||
      detail.accessSummary.calculationMethod.toString().toLowerCase() === CalculationMethodEnum.TimeOrDistance.toLowerCase()
    );
  }

  private getColumnGroups = () => {
    const columnGroups: Array<ColumnGroup<AdequacyDetailWaffleToppings>> = [
      {
        children: ["title", "resultsFor", "countySSA", "countyClass", "membershipName", "fullMembershipCount"],
        hideKey: true,
        key: "id",
        testId: "group-id",
      },
      {
        // 35 columns here... D:
        children: ["maxReqdDistance", "maxReqdTime", "fullMemberCountWithAccess", "accessWith", "fullMemberCountWithoutAccess", "accessWithout", "accessAvgDist", "accessAvgDist2", "accessAvgDistWith", "accessAvgDist2With", "accessAvgDistWithout", "accessAvgDist2Without", "accessWithDist", "accessWithoutDist", "accessMaxDist", "accessMaxDistWith", "accessMaxDistWithout", "accessAvgTime", "accessAvgTimeWith", "accessAvgTime2With", "accessAvgTimeWithout", "accessAvgTime2Without", "accessAvgTime2", "accessMaxTime", "accessMaxTimeWith", "accessMaxTimeWithout", "accessWithTime", "accessWithoutTime", "accessMetDist", "accessMetTime", "accessMet", "accessTelehealthCredit", "conAccessCreditPercentage"],
        hideKey: false,
        key: "Access Requirements",
        testId: "group-access-requirements",
      },
      {
        children: ["servicingRequired", "servicingProviderCount", "servicingDistinct", "servicingProviderToMemberRatio", "servicingMet"],
        key: "Servicing Provider Requirements",
        testId: "group-servicing-requirements",
      },
      {
        children: ["overallMet", "overallGapAge"],
        key: "Overall",
        testId: "group-overall",
      },
      {
        children: ["marketAccess", "marketProviderCount", "marketDistinct", "marketPercentage"],
        key: "Market Analysis",
        showInfoIcon: true,
        testId: "group-market-analysis",
        tooltip: getMarketAnalysisTooltip(this.props.runConfiguration)
      },
      {
        children: ["prospectImpact"],
        key: "Prospect",
        testId: "group-prospect",
      },
      {
        children: ["attestationPercent"],
        hideKey: true,
        key: "attestation",
        testId: "group-attestation",
      },
      {
        children: ["fillableGapIndicator", "gapFillStatus", "gapFillStatusUpdatedDate", "gapFillStatusUpdatedByName"],
        key: "Gap Resolution",
        testId: "group-gap-resolution",
      },
    ];

    return columnGroups;
  }

  private filterRecords = (row: AdequacyDetailWaffleToppings, columnFilter: ColumnFilter) => {
    const colKey = columnFilter.key;
    if (colKey === "title") {
      // type: string, but it's an element. That's a special case.
      switch (columnFilter.value.operator) {
        case "contains":
          return (row.title.key as string).toLowerCase().includes((columnFilter.value.value as string).toLowerCase());
        case "does not contain":
          return !(row.title.key as string).toLowerCase().includes((columnFilter.value.value as string).toLowerCase());
      }
    }

    return genericFilter<AdequacyDetailWaffleToppings>(row, colKey, columnFilter);
  }

  private buildMappingLink = (props: AdequacyDetailsProps, visible: boolean) => {
    if (!visible) {
      return null;
    }

    return (
      <span className="ml-3">
        <Link
          to={{ pathname: props.mappingEndpoint }}
          data-testid="adequacy-details__mapping-button"
        >Create a Map
        </Link>
      </span>
    );
  }
}

const WrappedAdequacyDetails = withGapFillStatusDescriptions(withFeatureFlagContext(withUserContext(withViewableFields(withCountyProjectContext(AdequacyDetails)))));

export { WrappedAdequacyDetails as AdequacyDetails };

function getImpactIndicator(impactIndicator: ImpactIndicator, metCriteria: boolean): JSX.Element {

  // no dot of any kind if the row already passes. ImpactIndicator is unreliable, at least for older results
  if (metCriteria) {
    return (<span key="0" />);
  }

  switch (impactIndicator) {
    case ImpactIndicator.EmptyDot:
      return (
        <TooltipWrapper tooltipWidth="150px" text="Single provider can increase member access percentage" key="1">
          <span className="text-warning"><small><i className="fa fa-circle-o" /></small></span>
        </TooltipWrapper>
      );
    case ImpactIndicator.FullDot:
      return (
        <TooltipWrapper tooltipWidth="150px" text="Single provider can bring member access percentage to or above measurement threshold" key="2">
          <span className="text-success"><small><i className="fa fa-circle" /></small></span>
        </TooltipWrapper>
      );
    case ImpactIndicator.None:
    default:
      return (<span key="0" />);
  }
}

function SkeletonResults(props: { isComparingResults?: boolean, isLowestGranularity?: boolean, runConfiguration?: ProjectRunConfigurationResult, shimmer: boolean }): JSX.Element {
  const className = `skeleton ${props.shimmer ? "shimmer" : ""}`;
  if (!props.isLowestGranularity) {
    return (
      <span className={className}>
        <SkeletonTable />
      </span>
    );
  }

  return (
    <span className={className}>
      <SkeletonTable rows={1} />

      {(!props.isComparingResults || null) && (
        <div className="specialty-group-exception-editor-trigger">
          <span className="bone">Enter&nbsp;an&nbsp;Exception</span>
        </div>
      )}

      <hr className="mt0" />
      <p className="plr20 mb30 small">
        <span className="bone">
          <i className="fa fa-info-circle mr5" />
          GeographyName is a GeoClass GeoType where networks are required to cover 00% of the
          beneficiaries with access to ct SpecialtyGroup EntityType within DistancePhrase.
        </span>
      </p>

      {(!props.isComparingResults || null) && (
        <SkeletonPanel className="mt50 mb0" dark={true} title="Network Servicing Providers" transparent={true} />
      )}
      {((!props.isComparingResults && props.runConfiguration && props.runConfiguration.prospectProviderSetId) || null) && (
        <SkeletonPanel className="mb0" dark={true} title="Prospect Servicing Providers" transparent={true} />
      )}
      {((!props.isComparingResults && props.runConfiguration && props.runConfiguration.useMarketProvidersForImpact) || null) && (
        <SkeletonPanel className="mb0" dark={true} title="Market Servicing Providers" transparent={true} />
      )}
    </span>
  );
}
